import React from 'react';
import type { PatientRecord } from '../../patients/types';
import { Avatar } from '../Avatar/Avatar';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { ppEyebrowClass } from '../../patients/utils';
import { PatientLanguageSelect } from '../PatientLanguage/PatientLanguage';
import type { LanguageCode, LanguageSource } from '../../platform-languages';

export interface PatientProfileHeroProps {
  patient: PatientRecord;
  onMessage?: () => void;
  /** Amendment 25 §5 — every message to this patient goes in this language. */
  onSetLanguage?: ((next: LanguageCode) => void) | undefined;
  onOpenLanguages?: (() => void) | undefined;
}

export function PatientProfileHero({
  patient: p,
  onMessage,
  onSetLanguage,
  onOpenLanguages,
}: PatientProfileHeroProps) {
  const [nudgeDone, setNudgeDone] = React.useState(false);
  const [language, setLanguage] = React.useState<LanguageCode>(p.languageCode ?? 'en');
  const [source, setSource] = React.useState<LanguageSource>(p.languageSource ?? { kind: 'default' });
  const choose = (next: LanguageCode) => {
    setLanguage(next);
    // Choosing it here IS the source — a staff decision has to be
    // distinguishable from whatever the PMS happened to hold (§5).
    setSource({ kind: 'staff', by: 'You', on: 'just now' });
    onSetLanguage?.(next);
  };
  return (
    <div className="pp-fade relative mb-4 overflow-hidden rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-[26px_28px] shadow-[var(--sh-2)]">
      <span
        aria-hidden
        className="absolute left-0 right-0 top-0 h-[3px] bg-[var(--grad-spectrum)]"
      />
      <span
        aria-hidden
        className="pointer-events-none absolute right-[-40px] top-[-100px] h-[220px] w-[340px] blur-[24px]"
        style={{
          background:
            'radial-gradient(closest-side, color-mix(in srgb, var(--ai-3) 16%, transparent), transparent)',
        }}
      />
      <div className="relative flex flex-wrap items-center gap-[18px]">
        <Avatar name={p.name} hue="var(--ai-3)" size={64} />
        <div className="min-w-0 flex-[1_1_240px]">
          <div className="mb-2 flex items-center gap-2">
            <Icon kind="check-circ" size={13} stroke={2} style={{ color: 'var(--success)' }} />
            <span className={ppEyebrowClass} style={{ color: 'var(--success)' }}>
              {p.status}
            </span>
            <span
              className="ml-1 inline-flex items-center gap-1 rounded-[var(--r-pill)] px-2 py-0.5 text-[11px] font-semibold"
              style={{
                background: p.consent?.granted ? 'var(--success-tint)' : 'var(--cream-0)',
                color: p.consent?.granted ? 'var(--success)' : 'var(--ink-3)',
                border: p.consent?.granted ? 'none' : '1px solid var(--cream-2)',
              }}
              title={
                p.consent?.granted
                  ? `ELVA essentials consent granted${p.consent.grantedAt ? ` ${p.consent.grantedAt}` : ''} — data may be used for the services this practice runs on, including marketing`
                  : 'ELVA essentials consent not granted — this patient’s data may not be used for marketing'
              }
            >
              <Icon kind={p.consent?.granted ? 'check' : 'x'} size={10} stroke={2.6} />
              ELVA essentials
            </span>
          </div>
          <h1 className="m-0 font-[var(--font-display)] text-[clamp(28px,4vw,38px)] font-bold leading-[1.04] tracking-[-0.04em] text-[var(--ink-0)]">
            {p.first} <span className="pp-spectrum">{p.last}</span>
          </h1>
          <div className="mt-2.5 flex flex-wrap items-center gap-3.5 text-[13px] text-[var(--ink-2)]">
            <span>
              {p.age} yrs · Patient since {p.since}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Icon kind="phone" size={12} stroke={1.8} style={{ color: 'var(--ink-4)' }} />
              {p.phone}
            </span>
            {p.contact?.smsOptedOut ? (
              <span
                className="inline-flex items-center gap-1 rounded-[var(--r-pill)] bg-[var(--terracotta-tint)] px-2 py-0.5 text-[11px] font-semibold text-[var(--terracotta)]"
                title="This patient has opted out of SMS — do not text this number"
              >
                <Icon kind="x" size={10} stroke={2.4} />
                SMS off
              </span>
            ) : null}
            <span className="inline-flex items-center gap-1.5">
              <Icon kind="mail" size={12} stroke={1.8} style={{ color: 'var(--ink-4)' }} />
              {p.email}
            </span>
            {p.contact?.preferredChannel ? (
              <span className="text-[12px] text-[var(--ink-3)]">
                prefers {p.contact.preferredChannel}
                {p.contact.bestTime ? ` · ${p.contact.bestTime}` : ''}
              </span>
            ) : null}
            <span className="font-[var(--font-mono)] text-[var(--ink-3)]">{p.visits} visits</span>
          </div>
        </div>
        <div className="flex shrink-0 flex-nowrap items-center gap-3">
          {/* The language belongs on the header, not in a messaging tab: it
              governs texts, emails and calls from every agent (§5). */}
          <label className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-[9px] border border-[var(--cream-2)] bg-[var(--cream-1)] px-2.5 py-1.5 text-[12px] font-semibold text-[var(--ink-1)]">
            <input
              type="checkbox"
              defaultChecked={p.wantsEarlier ?? false}
              aria-label="Wants an earlier appointment if one opens up"
            />
            Wants an earlier appointment if one opens up
          </label>
          <PatientLanguageSelect
            value={language}
            source={source}
            nudge={nudgeDone ? undefined : p.languageNudge}
            onChange={choose}
            onAcceptNudge={(code) => {
              choose(code);
              setNudgeDone(true);
            }}
            onDismissNudge={() => {
              setNudgeDone(true);
            }}
            onOpenLanguages={onOpenLanguages}
          />
          <Button variant="secondary" size="sm" iconLeft="chat" onClick={onMessage}>
            Message
          </Button>
        </div>
      </div>
    </div>
  );
}
